import { useEffect, useRef, useState } from 'react';
import { getImagePath } from '../utils/imagePath';
import EditableText from './EditableText';

const HERO_LOGO = '/images/logos/white_heart.png';

const Hero = () => {
  const heroContentRef = useRef(null);
  const grainRef = useRef(null);
  const heroImageRef = useRef(null);
  const heroImageContainerRef = useRef(null);
  const heroImageElementRef = useRef(null);
  const purpleImageRef = useRef(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = useState(false);

  useEffect(() => {
    let rafId = null;

    const updateParallax = () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }

      rafId = requestAnimationFrame(() => {
        const hero = document.getElementById('home');
        if (!hero) return;

        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const heroTop = hero.offsetTop;
        const heroHeight = hero.offsetHeight;
        const windowHeight = window.innerHeight;

        const isHeroInView = scrollTop + windowHeight >= heroTop && scrollTop <= heroTop + heroHeight + 200;

        if (!isHeroInView) {
          if (grainRef.current) grainRef.current.style.transform = 'translate(0, 0)';
          if (heroContentRef.current) heroContentRef.current.style.transform = 'translate3d(0, 0, 0)';
          return;
        }

        const relativeScroll = scrollTop - heroTop + windowHeight;

        // Background grain moves slower
        if (grainRef.current) {
          const grainOffset = relativeScroll * 0.15;
          grainRef.current.style.transform = `translate3d(${grainOffset * 0.3}px, ${grainOffset * 0.3}px, 0)`;
        }

        // Hero content has small parallax
        if (heroContentRef.current) {
          const contentSpeed = 0.05;
          const maxOffset = heroHeight * 0.08;
          const contentOffset = Math.min(maxOffset, relativeScroll * contentSpeed);
          heroContentRef.current.style.transform = `translate3d(0, ${contentOffset}px, 0)`;
        }
      });
    };

    const handleScroll = () => {
      updateParallax();
    };

    window.addEventListener('scroll', handleScroll);
    updateParallax();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, []);

  // Handle mouse movement for circular reveal effect
  useEffect(() => {
    const handleMouseMove = (e) => {
      // Use the wrapper container for coordinate calculation to ensure consistency
      const containerElement = heroImageRef.current;
      if (!containerElement) return;
      
      const rect = containerElement.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      setMousePosition({ x, y });
    };

    const handleMouseEnter = () => {
      setIsHovering(true);
    };

    const handleMouseLeave = () => {
      setIsHovering(false);
    };

    // Add listeners to multiple elements to catch hover over content area too
    const containerElement = heroImageContainerRef.current;
    const heroSection = document.getElementById('home');
    const contentWrapper = heroContentRef.current?.parentElement;

    if (containerElement) {
      containerElement.addEventListener('mousemove', handleMouseMove);
      containerElement.addEventListener('mouseenter', handleMouseEnter);
      containerElement.addEventListener('mouseleave', handleMouseLeave);
    }

    if (heroSection) {
      heroSection.addEventListener('mousemove', handleMouseMove);
      heroSection.addEventListener('mouseenter', handleMouseEnter);
      heroSection.addEventListener('mouseleave', handleMouseLeave);
    }

    if (contentWrapper) {
      contentWrapper.addEventListener('mousemove', handleMouseMove);
      contentWrapper.addEventListener('mouseenter', handleMouseEnter);
      contentWrapper.addEventListener('mouseleave', handleMouseLeave);
    }

    return () => {
      if (containerElement) {
        containerElement.removeEventListener('mousemove', handleMouseMove);
        containerElement.removeEventListener('mouseenter', handleMouseEnter);
        containerElement.removeEventListener('mouseleave', handleMouseLeave);
      }
      if (heroSection) {
        heroSection.removeEventListener('mousemove', handleMouseMove);
        heroSection.removeEventListener('mouseenter', handleMouseEnter);
        heroSection.removeEventListener('mouseleave', handleMouseLeave);
      }
      if (contentWrapper) {
        contentWrapper.removeEventListener('mousemove', handleMouseMove);
        contentWrapper.removeEventListener('mouseenter', handleMouseEnter);
        contentWrapper.removeEventListener('mouseleave', handleMouseLeave);
      }
    };
  }, []);

  const handleGetStarted = (e) => {
    e.preventDefault();
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      const navbar = document.querySelector('[data-navbar]');
      const navbarHeight = navbar ? navbar.offsetHeight : 140;
      const targetPosition = contactSection.offsetTop - navbarHeight;
      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section
      id="home"
      className="min-h-screen h-screen flex items-start justify-center relative bg-[#1a1a1a] pt-12 md:pt-16 lg:pt-20 p-0 m-0 z-[1] w-full max-w-[100vw] overflow-x-hidden"
    >
      {/* Sticky background layer */}
      <div className="fixed top-0 left-0 w-full h-screen bg-[#1a1a1a] -z-[2]" />

      {/* Background grain texture - fixed position */}
      <div
        ref={grainRef}
        className="fixed top-0 left-0 w-full h-full opacity-[0.025] pointer-events-none -z-[2] overflow-hidden"
        style={{
          backgroundImage: `
            repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255, 255, 255, 0.08) 2px, rgba(255, 255, 255, 0.08) 4px),
            repeating-linear-gradient(90deg, transparent, transparent 2px, rgba(255, 255, 255, 0.08) 2px, rgba(255, 255, 255, 0.08) 4px)
          `,
          backgroundSize: '120px 120px',
          willChange: 'transform',
          transition: 'transform 0.1s ease-out'
        }}
      />

      {/* Full Hero Image Container */}
      <div 
        ref={heroImageContainerRef}
        className="fixed -z-[1] left-0 right-0 w-screen h-full overflow-hidden flex items-end justify-center" 
        style={{ bottom: 0, pointerEvents: 'auto' }}
      >
        <div
          ref={heroImageRef}
          style={{
            position: 'relative',
            width: '100vw',
            maxWidth: 'none',
            maxHeight: '80vh',
            height: 'auto',
            pointerEvents: 'auto'
          }}
        >
          {/* Spacer to give wrapper height - mobile vs desktop image for correct aspect */}
          <img
            src={getImagePath("/images/heroImages/mobile-optimized-hero.png")}
            alt=""
            className="md:hidden block w-full h-auto"
            style={{ visibility: 'hidden', pointerEvents: 'none' }}
            aria-hidden
          />
          <img
            src={getImagePath("/images/heroImages/hero-clean.png")}
            alt=""
            className="hidden md:block w-full h-auto"
            style={{ visibility: 'hidden', pointerEvents: 'none' }}
            aria-hidden
          />

          {/* Mobile: single optimized hero image */}
          <img
            src={getImagePath("/images/heroImages/mobile-optimized-hero.png")}
            alt=""
            className="md:hidden block absolute inset-0 w-full h-full object-contain object-bottom z-[2] pointer-events-none"
          />

          {/* Desktop: purple background - only shows circular area where cursor is */}
          <img
            ref={purpleImageRef}
            src={getImagePath("/images/heroImages/hero-full-purple.png")}
            alt="Hero purple background"
            className="hidden md:block"
            style={{
              display: isHovering ? 'block' : 'none',
              position: 'absolute',
              top: 0,
              left: '3px',
              right: 0,
              bottom: 0,
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              objectPosition: 'bottom',
              zIndex: 1,
              pointerEvents: 'none',
              maskImage: isHovering
                ? `radial-gradient(circle 80px at ${mousePosition.x}px ${mousePosition.y}px, black 0%, black 40%, rgba(0,0,0,0.8) 45%, rgba(0,0,0,0.3) 50%, transparent 55%, transparent 100%)`
                : 'none',
              WebkitMaskImage: isHovering
                ? `radial-gradient(circle 80px at ${mousePosition.x}px ${mousePosition.y}px, black 0%, black 40%, rgba(0,0,0,0.8) 45%, rgba(0,0,0,0.3) 50%, transparent 55%, transparent 100%)`
                : 'none',
              transition: 'mask-image 0.1s ease-out, -webkit-mask-image 0.1s ease-out'
            }}
          />

          {/* Desktop: hero image with circular mask reveal */}
          <img
            ref={heroImageElementRef}
            src={getImagePath("/images/heroImages/hero-clean.png")}
            alt="Hero collage"
            className="hidden md:block absolute inset-0 w-full h-full object-contain object-bottom z-[2] pointer-events-none"
            style={{
              backgroundColor: 'transparent',
              ...(isHovering && {
                maskImage: `radial-gradient(circle 80px at ${mousePosition.x}px ${mousePosition.y}px, transparent 0%, transparent 40%, rgba(0,0,0,0.2) 45%, rgba(0,0,0,0.7) 50%, black 55%, black 100%)`,
                WebkitMaskImage: `radial-gradient(circle 80px at ${mousePosition.x}px ${mousePosition.y}px, transparent 0%, transparent 40%, rgba(0,0,0,0.2) 45%, rgba(0,0,0,0.7) 50%, black 55%, black 100%)`
              })
            }}
          />
        </div>
      </div>

      {/* Hero content wrapper */}
      <div
        className="w-full flex items-start justify-between relative z-10 px-4 sm:px-6 lg:px-10 xl:px-12 m-0 box-border"
      >
        {/* Text content - on mobile: pushed down; on desktop normal */}
        <div
          className="text-left text-white z-10 flex flex-col p-0 m-0 mt-24 md:mt-0"
          data-parallax-speed="0.05"
          ref={heroContentRef}
        >
          <h1
            className="m-0 mb-2 font-semibold font-serif uppercase tracking-[2px] md:tracking-[2.5px] lg:tracking-[3px] xl:tracking-[4px] leading-[1.15] text-left block w-fit text-[1.75rem] sm:text-[1.875rem] md:text-[2rem] lg:text-[2.5rem] xl:text-[3rem] 2xl:text-[3.5rem] opacity-0 translate-y-[50px] animate-[slideUpText_1s_ease-out_0.3s_forwards] text-white max-w-[9rem] md:max-w-full"
          >
            <EditableText path="hero.taglineLine1" fallback="Purpose" />
            <span className="hidden md:inline">&nbsp;</span>
            <br className="md:hidden" />
            <span className="whitespace-nowrap md:whitespace-normal">
              <EditableText path="hero.taglineLine2" fallback="driven messaging" />
            </span>
          </h1>
          <p
            className="text-[1.125rem] sm:text-[1.25rem] md:text-[1.875rem] lg:text-[2.25rem] xl:text-[2.5rem] 2xl:text-[3rem] mb-2 opacity-0 translate-y-[50px] animate-[slideUpText_1s_ease-out_0.8s_forwards] relative font-semibold font-serif uppercase tracking-[2px] md:tracking-[2.5px] lg:tracking-[3px] xl:tracking-[4px] leading-[1.15] max-w-full whitespace-nowrap md:whitespace-normal"
          >
            <EditableText path="hero.subtitle" fallback="Texts · Calls · Townhalls" className="text-[#9a83ad]" />
          </p>
          <p
            className="text-[1.125rem] sm:text-[1.25rem] md:text-[1.625rem] lg:text-[2rem] xl:text-[2.25rem] 2xl:text-[2.75rem] mb-8 opacity-0 translate-y-[50px] animate-[slideUpText_1s_ease-out_1.1s_forwards] font-semibold font-serif uppercase tracking-[2px] md:tracking-[2.5px] lg:tracking-[3px] xl:tracking-[4px] text-white leading-[1.15] max-w-full"
          >
            <EditableText path="hero.subtitle2" fallback="Trusted Results" />
          </p>
          <button 
            className="px-8 py-3 text-base bg-white text-[#0a0a0a] border border-white rounded-lg cursor-pointer font-semibold uppercase tracking-[2px] transition-all duration-[400ms] ease-[cubic-bezier(0.4,0,0.2,1)] shadow-none font-serif relative overflow-hidden opacity-0 translate-y-[50px] animate-[slideUpText_1s_ease-out_1.5s_forwards] hover:-translate-y-0.5 hover:shadow-lg hover:text-white before:content-[''] before:absolute before:top-0 before:-left-full before:w-full before:h-full before:bg-[#9a83ad] before:transition-[left] before:duration-[500ms] before:ease-[cubic-bezier(0.4,0,0.2,1)] before:-z-10 hover:before:left-0 active:translate-y-0 w-fit"
            onClick={handleGetStarted}
          >
            <EditableText path="hero.cta" fallback="Get Started" tag="span" />
          </button>
        </div>

        {/* Logo - default hero logo */}
        <div className="absolute right-16 md:right-[0.5%] lg:right-[1%] top-[8%] md:top-[6%] lg:top-[8%] z-[2] pointer-events-none flex items-center justify-end flex-shrink-0 w-[150px] md:w-[180px] lg:w-[250px] xl:w-[300px] 2xl:w-[350px] max-w-[35%] md:max-w-[30%]">
          <img
            src={getImagePath(HERO_LOGO)}
            alt="VBC Group"
            className="w-full h-auto block object-contain opacity-0 translate-x-[100px] animate-[slideInFromRight_1s_ease-out_0.5s_forwards] transition-opacity duration-500"
          />
        </div>
      </div>
    </section>
  );
};

export default Hero;
